import { cn } from '@zealot/ui/lib/utils';
import { Loader2 } from 'lucide-react';
import { Toaster as SonnerToaster, toast } from 'sonner';

type ToasterProps = React.ComponentProps<typeof SonnerToaster>;

// gloss
const TOAST_CLASS = cn(
  'group relative flex w-full items-stretch overflow-hidden rounded-xl border border-border bg-background shadow-lg',
  'font-sans',
  '[&[data-expanded=false][data-front=false]>*]:opacity-0',
  '[&:has([data-close-button])_[data-button]]:mr-10',
);

// gloss
const ICON_CLASS = 'relative flex w-11 shrink-0 items-center justify-center self-stretch [&_svg]:size-5';

const TONE_CLASS = {
  default: '[&_[data-icon]]:bg-muted [&_[data-icon]]:text-muted-foreground',
  success: '[&_[data-icon]]:bg-success-muted [&_[data-icon]]:text-success',
  error: '[&_[data-icon]]:bg-destructive-muted [&_[data-icon]]:text-destructive',
  warning: '[&_[data-icon]]:bg-warning-muted [&_[data-icon]]:text-warning',
  info: '[&_[data-icon]]:bg-info-muted [&_[data-icon]]:text-info',
  loading: '[&_[data-icon]]:bg-muted [&_[data-icon]]:text-muted-foreground',
} as const;

// gloss
const TOAST_ICONS = { loading: <Loader2 className="animate-spin" /> } as const;

// why: sonner's 356px cannot hold a sentence beside an action button — the text collapses to a column.
const TOAST_WIDTH = '28rem';

// gloss
const Toaster = ({
  className,
  toastOptions,
  theme = 'light',
  position = 'bottom-right',
  closeButton = true,
  icons,
  style,
  ...props
}: ToasterProps) => {
  return (
    <SonnerToaster
      data-slot="sonner-toaster"
      theme={theme}
      position={position}
      style={{ '--width': TOAST_WIDTH, ...style } as React.CSSProperties}
      closeButton={closeButton}
      icons={{ ...TOAST_ICONS, ...icons }}
      className={cn('toaster', className)}
      toastOptions={{
        unstyled: true,
        ...toastOptions,
        classNames: {
          toast: TOAST_CLASS,
          icon: ICON_CLASS,
          content: 'flex min-w-0 flex-1 flex-col gap-0.5 py-3 pr-9 pl-4',
          title: 'font-semibold text-foreground text-sm',
          description: 'text-muted-foreground text-xs',
          closeButton: cn(
            'absolute top-1/2 right-4 -translate-y-1/2 cursor-pointer rounded-md border-0 bg-transparent p-0 text-muted-foreground opacity-70',
            'transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none [&_svg]:size-4',
          ),
          actionButton:
            'ml-auto mr-4 shrink-0 self-center rounded-md bg-primary px-3 py-1.5 font-medium text-primary-foreground text-xs',
          cancelButton:
            'shrink-0 self-center rounded-md bg-muted px-3 py-1.5 font-medium text-muted-foreground text-xs',
          ...TONE_CLASS,
          ...toastOptions?.classNames,
        },
      }}
      {...props}
    />
  );
};

export type { ToasterProps };
export { Toaster, toast };
